/**
 * Which brand's portal a parent is currently looking at.
 *
 * One account, one set of children, one wallet — two catalogs. The active profile decides
 * which programs and which enrollments a parent sees; it decides nothing about what they are
 * *allowed* to see, which stays entirely with RLS. Switching profiles is a view change, not a
 * login.
 *
 * Resolution order for the active brand, highest first:
 *
 *   1. The brand of the page a link landed on — an invoice email, a shared program link. The
 *      page reports its subject's brand through `alignLandingBrand` once it has loaded. Sticky,
 *      like `?brand=`, because it is the same thing: the parent followed a link into one brand.
 *   2. `?brand=` on the URL — how a Kaleido-first family arrives (a Kaleido link, eventually a
 *      Kaleido host). Sticky: it is saved like a switcher choice, so the parameter is needed once.
 *   3. A choice made in this tab (the switcher, the sign-up picker), or 1 or 2 above. Held
 *      in memory so it applies at once. It is saved to 4 and 5 as well.
 *   4. The last profile this account chose, from `parents.brand_preference`, so the choice
 *      follows the parent to a second device.
 *   5. The same, from `localStorage`. Used when the account's value is unavailable: not loaded
 *      yet, offline, or never saved.
 *   6. The single brand the family already has enrollments in — a BHCS-only family lands in
 *      BHCS, a Kaleido-only family in Kaleido, without either ever touching the switcher.
 *   7. `DEFAULT_BRAND_KEY`.
 *
 * The account wins over the device: a parent who switched on their laptop, then opens the
 * phone that still remembers the old profile, should see the laptop's choice. A save that
 * fails still shows the choice in this tab, and it stays on this device until the account
 * has one.
 *
 * Storage is keyed by user id so two accounts sharing a browser do not inherit each other's
 * profile, and every access is wrapped: Safari private mode throws on `localStorage`, and a
 * portal that cannot render because a preference could not be read is worse than one that
 * opens on the default brand.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { BRAND_KEYS, DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../config/brands';
import { BrandProfileContext, type BrandProfileContextValue } from './brand-profile';
import { trackBrandSwitch, type BrandSwitchSource } from '../utils/analytics';

const STORAGE_PREFIX = 'bhcs_brand_profile';

/**
 * Routes a parent passes through on the way to somewhere else: sign-in, the OAuth callback that
 * then navigates to the link's destination, and the public home that forwards a signed-in parent
 * to the dashboard. None of them is ever the page a visit landed on, and passing through one is
 * not leaving that page.
 */
const FORWARDING_PATHS: ReadonlySet<string> = new Set(['/', '/login', '/auth/callback']);

function ignoreLandingBrand(): void {}

function storageKey(userId: string | undefined): string {
    return userId ? `${STORAGE_PREFIX}:${userId}` : STORAGE_PREFIX;
}

function readStoredBrand(userId: string | undefined): BrandKey | null {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        return isBrandKey(raw) ? raw : null;
    } catch {
        return null;
    }
}

function writeStoredBrand(userId: string | undefined, brand: BrandKey): void {
    try {
        localStorage.setItem(storageKey(userId), brand);
    } catch {
        // Private browsing, or storage full. The choice lasts for this render tree either way.
    }
}

/** A choice made in this tab, tagged with the account that made it. */
interface SessionChoice {
    owner: string | undefined;
    brand: BrandKey;
}

export function BrandProfileProvider({ children }: { children: ReactNode }) {
    const { user, isLoading: isAuthLoading } = useAuth();
    const userId = user?.id;
    const email = user?.email;
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const brandParam = searchParams.get('brand');
    const { pathname } = useLocation();

    /**
     * Brands the family has bought into, from their children's enrollments.
     *
     * No `student_id` filter: the parent SELECT policy on `enrollments` already narrows to
     * this family's rows, so adding a filter would only duplicate — and risk disagreeing
     * with — the rule the database is already applying. `programs!inner` is deliberate: a
     * `.eq` on an embedded column keeps the row and nulls the embed, which would report every
     * enrollment as brandless.
     */
    const { data: memberBrands = [], isLoading } = useQuery<BrandKey[]>({
        queryKey: ['brand-membership', userId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('enrollments')
                .select('programs!inner(brand_key)');
            if (error) throw error;
            const rows = (data ?? []) as unknown as Array<{ programs: { brand_key: string } | null }>;
            const found = new Set<BrandKey>();
            for (const row of rows) {
                const key = row.programs?.brand_key;
                // A row selected before `brand_key` existed reads as the default brand, which
                // is what the column's own DEFAULT would have given it.
                found.add(isBrandKey(key) ? key : DEFAULT_BRAND_KEY);
            }
            return BRAND_KEYS.filter(k => found.has(k));
        },
        enabled: !!user,
        staleTime: 5 * 60 * 1000,
    });

    /**
     * The profile this account last chose, saved on the parent row.
     *
     * Filtered by email even though RLS narrows `parents` to the caller's own row for a parent:
     * an admin or teacher who is also a parent reads every row through `is_admin()` /
     * `is_staff()`, and `maybeSingle` over all of them would error. `null` when there is no
     * parent row yet (sign-up) or the account has never chosen.
     */
    const preferenceKey = ['brand-preference', userId] as const;
    const { data: accountBrand = null, isLoading: isPreferenceLoading } = useQuery<BrandKey | null>({
        queryKey: preferenceKey,
        queryFn: async () => {
            const { data, error } = await supabase
                .from('parents')
                .select('brand_preference')
                .eq('email', email!)
                .maybeSingle();
            if (error) throw error;
            const saved = (data as { brand_preference: string | null } | null)?.brand_preference;
            return isBrandKey(saved) ? saved : null;
        },
        enabled: !!email,
        staleTime: 5 * 60 * 1000,
    });

    const { mutate: saveAccountBrand } = useMutation({
        mutationFn: async (brand: BrandKey) => {
            const { error } = await supabase
                .from('parents')
                .update({ brand_preference: brand })
                .eq('email', email!);
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: preferenceKey }),
        // The choice already shows in this tab and is saved on this device, so the parent loses
        // nothing now. Only their other devices miss out, so warn and do not interrupt them.
        onError: (error: unknown) =>
            console.warn('[brand-profile] could not save the profile to the account', error),
    });

    const [sessionChoice, setSessionChoice] = useState<SessionChoice | null>(() =>
        isBrandKey(brandParam) ? { owner: userId, brand: brandParam } : null
    );
    // A choice counts only for the account that made it. When a different user signs in on the
    // same tree, their saved profile applies, not the one left over in memory.
    const sessionBrand = sessionChoice && sessionChoice.owner === userId ? sessionChoice.brand : null;

    // Auth resolves after the first render, so storage is read again once the account is known.
    // Before that the read used the unkeyed slot, and keeping that value would let one account
    // inherit the brand another chose in the same browser.
    const storedBrand = useMemo(() => readStoredBrand(userId), [userId]);

    /**
     * The profile this account carries into a visit, before anything this tab did to it.
     *
     * Everything below the session choice in the resolution order, collapsed into one value:
     * the account, then the device, then an unambiguous membership, then the default. It is
     * what the parent would be looking at had they opened the portal with a bare URL, which
     * makes it the honest "from" for a switch a link performed — by the time that effect runs,
     * `activeBrand` has already absorbed the link.
     */
    const inheritedBrand: BrandKey =
        accountBrand ??
        storedBrand ??
        // Only an unambiguous membership picks for the parent. A family in both brands has no
        // "home" catalog to infer, so they get the default and the switcher.
        (memberBrands.length === 1 ? memberBrands[0] : DEFAULT_BRAND_KEY);

    const rememberBrand = useCallback(
        (brand: BrandKey, source: BrandSwitchSource, from: BrandKey) => {
            // Every switch passes through here, so this is the one place that can see both ends
            // of it — and the only one that cannot miss a route added later.
            //
            // A call that lands on the brand already in force is deliberately not a switch: it
            // is a no-op the parent never sees. `?brand=` is applied unconditionally, and a
            // parent may re-pick the profile they are already in.
            if (brand !== from) trackBrandSwitch({ from, to: brand, source, user });
            setSessionChoice({ owner: userId, brand });
            writeStoredBrand(userId, brand);
            // Before sign-up finishes there is no parent row, so this updates nothing, which is
            // fine: the sign-up insert writes the same value.
            if (email) saveAccountBrand(brand);
        },
        [userId, email, saveAccountBrand, user]
    );

    // `?brand=` wins, and is consumed once: it is persisted and stripped, so a shared or
    // bookmarked link does not pin the parent to that brand forever after they switch away.
    //
    // Not before the account is known. On a fresh page load the session arrives after the first
    // render, and a link consumed then is saved to nobody and stripped, so the parent opens on
    // their old profile. Signed out, it stays on the URL: the sign-in redirect carries it
    // (`redirectTo` keeps the query string) and it applies once they are in.
    useEffect(() => {
        if (!isBrandKey(brandParam) || isAuthLoading || !userId) return;
        rememberBrand(brandParam, 'link', inheritedBrand);
        const next = new URLSearchParams(searchParams);
        next.delete('brand');
        setSearchParams(next, { replace: true });
    }, [brandParam, isAuthLoading, userId, rememberBrand, inheritedBrand, searchParams, setSearchParams]);

    const chosenBrand: BrandKey | null = sessionBrand ?? accountBrand ?? storedBrand;

    const activeBrand: BrandKey = sessionBrand ?? inheritedBrand;

    const setActiveBrand = useCallback(
        (brand: BrandKey, source: BrandSwitchSource = 'switcher') => {
            rememberBrand(brand, source, activeBrand);
        },
        [rememberBrand, activeBrand]
    );

    /**
     * The page this visit landed on, for as long as it is still unanswered.
     *
     * `undefined` until the parent first reaches a page that is not a sign-in hop. A signed-out
     * parent's link is redirected to `/login` and comes back to the same path, and the hop in
     * between is skipped rather than counted as leaving, so the destination is still the
     * landing after sign-in. `null` once the page has reported its brand or the parent has
     * navigated anywhere else, and it never reopens: only a fresh page load starts a new visit.
     *
     * Tracked by path, not by history key, so a page that rewrites its own query string while
     * loading (`?tab=`, the `?brand=` strip above) is still the page the link opened.
     */
    const [landingPath, setLandingPath] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        if (FORWARDING_PATHS.has(pathname)) return;
        setLandingPath(prev => (prev === undefined ? pathname : prev === pathname ? prev : null));
    }, [pathname]);

    const alignLandingBrand = useMemo(() => {
        // Nothing left to align once the landing is over. Handing out the same no-op from then on
        // also stops the context value changing on every navigation for the rest of the visit.
        if (landingPath === null) return ignoreLandingBrand;
        return (subjectBrand: string | null | undefined) => {
            if (landingPath !== pathname || !isBrandKey(subjectBrand)) return;
            // Answered once. Leaving it open would fight the switcher: a parent who lands on a
            // Kaleido invoice and switches to BHCS would be pulled straight back.
            setLandingPath(null);
            // Only a real change is written. A link into the brand the parent is already in is
            // not a choice, and recording it would pin a derived profile as if it were one.
            if (subjectBrand !== activeBrand) setActiveBrand(subjectBrand, 'landing');
        };
    }, [landingPath, pathname, activeBrand, setActiveBrand]);

    const value = useMemo<BrandProfileContextValue>(
        () => ({
            activeBrand,
            setActiveBrand,
            memberBrands,
            availableBrands: BRAND_KEYS,
            hasChosenBrand: chosenBrand !== null,
            // Always true here; the context default (no provider) is what reports false.
            isMultiBrand: true,
            isLoading: isLoading || isPreferenceLoading,
            alignLandingBrand,
        }),
        [
            activeBrand,
            setActiveBrand,
            memberBrands,
            chosenBrand,
            isLoading,
            isPreferenceLoading,
            alignLandingBrand,
        ]
    );

    return <BrandProfileContext.Provider value={value}>{children}</BrandProfileContext.Provider>;
}
